import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getPolls = createAsyncThunk(
    'polls/getPolls',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.POLLS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Polls retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch polls',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch polls';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch polls';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getPoll = createAsyncThunk(
    'polls/getPoll',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.POLL(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Poll retrieved successfully',
                    poll: data?.poll || null,
                    statistics: data?.statistics || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch poll',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch poll';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch poll';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const createPoll = createAsyncThunk(
    'polls/createPoll',
    async (pollData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.POLL_CREATE, pollData);
            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Poll created successfully',
                    poll: data?.poll || data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to create poll',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to create poll';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to create poll';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const updatePoll = createAsyncThunk(
    'polls/updatePoll',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.put(API_ENDPOINTS.POLL_UPDATE(id), data);
            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Poll updated successfully',
                    poll: responseData?.poll || responseData || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update poll',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to update poll';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to update poll';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const deletePoll = createAsyncThunk(
    'polls/deletePoll',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.delete(API_ENDPOINTS.POLL_DELETE(id));
            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Poll deleted successfully',
                    id: id
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to delete poll',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to delete poll';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to delete poll';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const duplicatePoll = createAsyncThunk(
    'polls/duplicatePoll',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.POLL_DUPLICATE(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Poll duplicated successfully',
                    poll: data?.poll || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to duplicate poll',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to duplicate poll';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to duplicate poll';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const activatePoll = createAsyncThunk(
    'polls/activatePoll',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.POLL_ACTIVATE(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Poll activated successfully',
                    poll: data?.poll || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to activate poll',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to activate poll';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to activate poll';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const closePoll = createAsyncThunk(
    'polls/closePoll',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.POLL_CLOSE(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Poll closed successfully',
                    poll: data?.poll || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to close poll',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to close poll';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to close poll';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getPollResponses = createAsyncThunk(
    'polls/getPollResponses',
    async ({ id, params = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.POLL_RESPONSES(id), { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Poll responses retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch poll responses',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch poll responses';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch poll responses';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const submitPollResponse = createAsyncThunk(
    'polls/submitResponse',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.POLL_RESPONSES(id), data);
            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Response submitted successfully',
                    response: responseData?.response || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to submit response',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to submit response';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to submit response';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getPollStats = createAsyncThunk(
    'polls/getStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.POLL_STATS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Poll statistics retrieved successfully',
                    stats: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch poll statistics',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch poll statistics';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch poll statistics';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const exportPolls = createAsyncThunk(
    'polls/export',
    async ({ type, params = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.POLL_EXPORT(type), { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Export data generated successfully',
                    exportData: data || {},
                    type: type
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to export data',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to export data';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to export data';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// ============================================
// INITIAL STATE
// ============================================

const initialState = {
    polls: [],
    currentPoll: null,
    pollResponses: [],
    pollStats: {
        total_polls: 0,
        active_polls: 0,
        closed_polls: 0,
        draft_polls: 0,
        total_responses: 0,
        total_sent: 0,
        avg_response_rate: 0,
        polls_by_type: {},
        responses_by_month: []
    },
    exportData: null,
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    isLoading: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

// ============================================
// SLICE
// ============================================

const pollSlice = createSlice({
    name: 'polls',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearApiMessage: (state) => {
            state.apiMessage = null;
        },
        clearPollState: (state) => {
            state.polls = [];
            state.currentPoll = null;
            state.pollResponses = [];
            state.pollStats = initialState.pollStats;
            state.exportData = null;
            state.pagination = initialState.pagination;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
        },
        clearCurrentPoll: (state) => {
            state.currentPoll = null;
        },
        clearExportData: (state) => {
            state.exportData = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET POLLS =====
            .addCase(getPolls.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getPolls.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.polls = responseData.data || [];
                state.pagination = responseData.meta || initialState.pagination;
                if (responseData.meta?.stats) {
                    state.pollStats = { ...state.pollStats, ...responseData.meta.stats };
                }
                state.successMessage = payload.message || 'Polls retrieved successfully';
                state.apiMessage = payload.message || 'Polls retrieved successfully';
                state.error = null;
            })
            .addCase(getPolls.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch polls';
                state.apiMessage = action.payload?.message || 'Failed to fetch polls';
                state.successMessage = null;
                state.polls = [];
                state.pagination = initialState.pagination;
            })

            // ===== GET POLL =====
            .addCase(getPoll.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getPoll.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentPoll = payload.poll || null;
                state.successMessage = payload.message || 'Poll retrieved successfully';
                state.apiMessage = payload.message || 'Poll retrieved successfully';
                state.error = null;
            })
            .addCase(getPoll.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch poll';
                state.apiMessage = action.payload?.message || 'Failed to fetch poll';
                state.successMessage = null;
                state.currentPoll = null;
            })

            // ===== CREATE POLL =====
            .addCase(createPoll.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createPoll.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Poll created successfully';
                state.apiMessage = payload.message || 'Poll created successfully';
                state.error = null;
                if (payload.poll) {
                    state.polls = [payload.poll, ...state.polls];
                }
            })
            .addCase(createPoll.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create poll';
                state.apiMessage = action.payload?.message || 'Failed to create poll';
                state.successMessage = null;
            })

            // ===== UPDATE POLL =====
            .addCase(updatePoll.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updatePoll.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Poll updated successfully';
                state.apiMessage = payload.message || 'Poll updated successfully';
                state.error = null;
                if (payload.poll) {
                    state.polls = state.polls.map(p => 
                        p.id === payload.poll.id ? payload.poll : p
                    );
                    if (state.currentPoll?.id === payload.poll.id) {
                        state.currentPoll = payload.poll;
                    }
                }
            })
            .addCase(updatePoll.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update poll';
                state.apiMessage = action.payload?.message || 'Failed to update poll';
                state.successMessage = null;
            })

            // ===== DELETE POLL =====
            .addCase(deletePoll.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deletePoll.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Poll deleted successfully';
                state.apiMessage = payload.message || 'Poll deleted successfully';
                state.error = null;
                if (payload.id) {
                    state.polls = state.polls.filter(p => p.id !== payload.id);
                    if (state.currentPoll?.id === payload.id) {
                        state.currentPoll = null;
                    }
                }
            })
            .addCase(deletePoll.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete poll';
                state.apiMessage = action.payload?.message || 'Failed to delete poll';
                state.successMessage = null;
            })

            // ===== DUPLICATE POLL =====
            .addCase(duplicatePoll.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(duplicatePoll.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Poll duplicated successfully';
                state.apiMessage = payload.message || 'Poll duplicated successfully';
                state.error = null;
                if (payload.poll) {
                    state.polls = [payload.poll, ...state.polls];
                }
            })
            .addCase(duplicatePoll.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to duplicate poll';
                state.apiMessage = action.payload?.message || 'Failed to duplicate poll';
                state.successMessage = null;
            })

            // ===== ACTIVATE POLL =====
            .addCase(activatePoll.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(activatePoll.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Poll activated successfully';
                state.apiMessage = payload.message || 'Poll activated successfully';
                state.error = null;
                if (payload.poll) {
                    state.polls = state.polls.map(p => 
                        p.id === payload.poll.id ? payload.poll : p
                    );
                    if (state.currentPoll?.id === payload.poll.id) {
                        state.currentPoll = payload.poll;
                    }
                }
            })
            .addCase(activatePoll.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to activate poll';
                state.apiMessage = action.payload?.message || 'Failed to activate poll';
                state.successMessage = null;
            })

            // ===== CLOSE POLL =====
            .addCase(closePoll.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(closePoll.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Poll closed successfully';
                state.apiMessage = payload.message || 'Poll closed successfully';
                state.error = null;
                if (payload.poll) {
                    state.polls = state.polls.map(p => 
                        p.id === payload.poll.id ? payload.poll : p
                    );
                    if (state.currentPoll?.id === payload.poll.id) {
                        state.currentPoll = payload.poll;
                    }
                }
            })
            .addCase(closePoll.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to close poll';
                state.apiMessage = action.payload?.message || 'Failed to close poll';
                state.successMessage = null;
            })

            // ===== GET POLL RESPONSES =====
            .addCase(getPollResponses.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getPollResponses.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.pollResponses = responseData.data || [];
                state.successMessage = payload.message || 'Poll responses retrieved successfully';
                state.apiMessage = payload.message || 'Poll responses retrieved successfully';
                state.error = null;
            })
            .addCase(getPollResponses.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch poll responses';
                state.apiMessage = action.payload?.message || 'Failed to fetch poll responses';
                state.successMessage = null;
                state.pollResponses = [];
            })

            // ===== SUBMIT RESPONSE =====
            .addCase(submitPollResponse.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(submitPollResponse.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Response submitted successfully';
                state.apiMessage = payload.message || 'Response submitted successfully';
                state.error = null;
            })
            .addCase(submitPollResponse.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to submit response';
                state.apiMessage = action.payload?.message || 'Failed to submit response';
                state.successMessage = null;
            })

            // ===== GET POLL STATS =====
            .addCase(getPollStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getPollStats.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.pollStats = { ...state.pollStats, ...(payload.stats || {}) };
                state.successMessage = payload.message || 'Poll statistics retrieved successfully';
                state.apiMessage = payload.message || 'Poll statistics retrieved successfully';
                state.error = null;
            })
            .addCase(getPollStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch poll statistics';
                state.apiMessage = action.payload?.message || 'Failed to fetch poll statistics';
                state.successMessage = null;
            })

            // ===== EXPORT POLLS =====
            .addCase(exportPolls.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(exportPolls.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.exportData = payload.exportData || null;
                state.successMessage = payload.message || 'Export data generated successfully';
                state.apiMessage = payload.message || 'Export data generated successfully';
                state.error = null;
            })
            .addCase(exportPolls.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to export data';
                state.apiMessage = action.payload?.message || 'Failed to export data';
                state.successMessage = null;
                state.exportData = null;
            });
    },
});

// ============================================
// ACTIONS
// ============================================

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearPollState,
    clearCurrentPoll,
    clearExportData,
} = pollSlice.actions;

export const selectPolls = (state) => state.polls?.polls || [];
export const selectCurrentPoll = (state) => state.polls?.currentPoll || null;
export const selectPollResponses = (state) => state.polls?.pollResponses || [];
export const selectPollStats = (state) => state.polls?.pollStats || initialState.pollStats;
export const selectPollPagination = (state) => state.polls?.pagination || initialState.pagination;
export const selectPollLoading = (state) => state.polls?.isLoading || false;
export const selectPollError = (state) => state.polls?.error || null;
export const selectPollSuccess = (state) => state.polls?.successMessage || null;
export const selectPollApiMessage = (state) => state.polls?.apiMessage || null;
export const selectPollExportData = (state) => state.polls?.exportData || null;

export const selectPollStatsSummary = (state) => {
    const stats = selectPollStats(state);
    return {
        totalPolls: stats.total_polls || 0,
        activePolls: stats.active_polls || 0,
        closedPolls: stats.closed_polls || 0,
        draftPolls: stats.draft_polls || 0,
        totalResponses: stats.total_responses || 0,
        totalSent: stats.total_sent || 0,
        avgResponseRate: stats.avg_response_rate || 0,
        pollsByType: stats.polls_by_type || {},
        responsesByMonth: stats.responses_by_month || []
    };
};

export default pollSlice.reducer;